import { useEffect, useMemo, useState } from "react";
import { X } from "lucide-react";
import { LogGrepBar } from "../LogGrepBar";
import { ChartPauseButton } from "../ChartPauseButton";
import { useChartPause } from "../../hooks/useChartPause";
import { useChargerLog, type ChargerLogSource } from "../../hooks/useChargerLog";
import { useEscapeToClose } from "../../hooks/useEscapeToClose";
import { useUtcClock } from "../../hooks/useUtcClock";
import {
  filterLogLines,
  logGrepFilterActive,
  logGrepFilterLabel,
  parseLogGrepQuery,
} from "../../utils/logLineFilter";
import {
  buildMeterValuesIndexList,
  buildPreviousMeterValuesMap,
  formatMeterValuesLogBody,
  formatPnLine,
  isBcu12ChargerId,
  parseLogLineTs,
  readMeterValuesParsePref,
  writeMeterValuesParsePref,
} from "../../utils/ocppMeterValuesLogFormat";

function sourceLabel(source: ChargerLogSource) {
  return source === "ocpp" ? "OCPP log" : "WS log";
}

const STALE_LOG_MS = 15 * 60 * 1000;

const CHARGER_ID_RE = /^[A-Za-z0-9._*-]+$/;

const ALT_CHARGER_PLACEHOLDER = "Alt. charger";
const ALT_CHARGER_TITLE =
  "Alt. charger. Try to open the ocpp log for that alternative charger instead";

function normalizeChargerId(id: string): string {
  return id.replace(/^\//, "").trim();
}

function isValidChargerId(id: string): boolean {
  const normalized = normalizeChargerId(id);
  return normalized.length > 0 && CHARGER_ID_RE.test(normalized);
}

function isLogLineStale(line: string, nowMs = Date.now()): boolean {
  const ts = parseLogLineTs(line);
  if (ts == null) return false;
  return nowMs - ts > STALE_LOG_MS;
}

function formatLine(line: string) {
  const inIdx = line.indexOf(" in ");
  const outIdx = line.indexOf(" out ");
  const dirIdx = inIdx >= 0 ? inIdx : outIdx;
  if (dirIdx < 0) return { head: line, body: null, dir: null };

  const head = line.slice(0, dirIdx);
  const dir = inIdx >= 0 ? "in" : "out";
  const body = line.slice(dirIdx + dir.length + 2);
  return { head, dir, body };
}

function parseWsSession(line: string): string | null {
  const match = line.match(/session=(ws-\d+)/);
  return match?.[1] ?? null;
}

/** Lines are newest-first; an open is active when no matching close appears above it. */
function wsOpenIndicesWithoutClose(lines: string[]): Set<number> {
  const active = new Set<number>();
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    if (!line.includes("[open]")) continue;
    const session = parseWsSession(line);
    if (!session) continue;

    let closedAfter = false;
    for (let j = 0; j < i; j++) {
      if (lines[j].includes("[close]") && parseWsSession(lines[j]) === session) {
        closedAfter = true;
        break;
      }
    }
    if (!closedAfter) active.add(i);
  }
  return active;
}

export function ChargerLogPanel({
  chargerId,
  source,
  onClose,
  onChargerIdChange,
}: {
  chargerId: string;
  source: ChargerLogSource;
  onClose: () => void;
  onChargerIdChange?: (chargerId: string) => void;
}) {
  const { paused, togglePause } = useChartPause();
  const { data, loading, loadingMore, error, loadMore } = useChargerLog(
    chargerId,
    source,
    paused,
    1000,
  );
  const utcNow = useUtcClock();
  const nowMs = useMemo(() => Date.parse(utcNow), [utcNow]);
  const [grep, setGrep] = useState("");
  const [altCharger, setAltCharger] = useState("");
  const [parseMeterValues, setParseMeterValues] = useState(() => readMeterValuesParsePref());
  const [displayPn, setDisplayPn] = useState(false);

  useEffect(() => {
    setGrep("");
    setAltCharger("");
    setDisplayPn(false);
  }, [chargerId, source]);

  useEffect(() => {
    if (!parseMeterValues) setDisplayPn(false);
  }, [parseMeterValues]);

  const toggleParseMeterValues = () => {
    setParseMeterValues((prev) => {
      const next = !prev;
      writeMeterValuesParsePref(next);
      return next;
    });
  };

  const toggleDisplayPn = () => {
    setDisplayPn((prev) => !prev);
  };

  useEscapeToClose(onClose);

  const openAltCharger = () => {
    if (!onChargerIdChange) return;
    const next = normalizeChargerId(altCharger);
    if (!isValidChargerId(next) || next === chargerId) return;
    onChargerIdChange(next);
  };

  const grepFilter = useMemo(() => parseLogGrepQuery(grep), [grep]);
  const grepActive = logGrepFilterActive(grepFilter);
  const grepLabel = logGrepFilterLabel(grepFilter);

  const activeWsOpenIndices = useMemo(() => {
    if (source !== "ws" || !data?.lines.length) return new Set<number>();
    return wsOpenIndicesWithoutClose(data.lines);
  }, [source, data?.lines]);

  const displayLines = useMemo(() => {
    if (!data?.lines.length) return [];
    return data.lines
      .map((line, index) => ({ line, index }))
      .filter(({ line }) => filterLogLines([line], grepFilter).length > 0);
  }, [data?.lines, grepFilter]);

  const bcu12 = isBcu12ChargerId(chargerId);

  const prevMeterValuesByIndex = useMemo(() => {
    if (!parseMeterValues || source !== "ocpp" || !data?.lines.length) {
      return new Map<number, never>();
    }
    return buildPreviousMeterValuesMap(data.lines);
  }, [parseMeterValues, source, data?.lines]);

  const mvIndexList = useMemo(() => {
    if (!parseMeterValues || source !== "ocpp" || !data?.lines.length) {
      return [];
    }
    return buildMeterValuesIndexList(data.lines);
  }, [parseMeterValues, source, data?.lines]);

  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      <button
        type="button"
        className="absolute inset-0 bg-black/20"
        aria-label="Close charger log panel"
        onClick={onClose}
      />
      <aside className="relative flex h-full w-full max-w-2xl flex-col border-l border-border-subtle bg-card shadow-lg">
        <header className="flex items-center justify-between gap-2 border-b border-border-subtle px-4 py-3">
          <div className="min-w-0">
            <h2
              className="truncate font-semibold text-text-primary"
              style={{ fontSize: "var(--font-heading)" }}
            >
              {sourceLabel(source)} · {chargerId}
            </h2>
            {data?.filePath && (
              <p className="mt-0.5 truncate text-text-tertiary" style={{ fontSize: "var(--font-micro)" }}>
                {data.filePath}
                {data.truncated ? " · tail scan" : ""}
                {paused ? " · paused" : " · live 1s"}
              </p>
            )}
            <p className="mt-0.5 font-mono text-text-secondary" style={{ fontSize: "var(--font-micro)" }}>
              UTC now {utcNow}
            </p>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            {source === "ocpp" && (
              <button
                type="button"
                onClick={toggleParseMeterValues}
                className={[
                  "rounded-md border px-2.5 py-1 font-medium transition-colors",
                  parseMeterValues
                    ? "border-accent bg-accent text-white"
                    : "border-border-subtle bg-page text-text-secondary hover:text-text-primary",
                ].join(" ")}
                style={{ fontSize: "var(--font-micro)" }}
                title="Parse MeterValues into I / P / E summary"
              >
                Parse MV
              </button>
            )}
            {source === "ocpp" && parseMeterValues && (
              <button
                type="button"
                onClick={toggleDisplayPn}
                className={[
                  "rounded-md border px-2.5 py-1 font-medium transition-colors",
                  displayPn
                    ? "border-accent bg-accent text-white"
                    : "border-border-subtle bg-page text-text-secondary hover:text-text-primary",
                ].join(" ")}
                style={{ fontSize: "var(--font-micro)" }}
                title="Rolling average P over past MV samples (payload t1); omits n when history is shorter"
              >
                Display Pn
              </button>
            )}
            <ChartPauseButton paused={paused} onToggle={togglePause} />
            <button
              type="button"
              onClick={onClose}
              className="rounded-md p-1.5 text-text-secondary hover:bg-page hover:text-text-primary"
              aria-label="Close"
            >
              <X size={18} strokeWidth={1.75} />
            </button>
          </div>
        </header>

        <LogGrepBar grep={grep} onGrepChange={setGrep} />

        <div className="min-h-0 flex-1 overflow-y-auto p-4">
          {loading && !data && (
            <p className="text-text-secondary" style={{ fontSize: "var(--font-body)" }}>
              Loading log…
            </p>
          )}
          {error && (
            <p className="text-status-error" style={{ fontSize: "var(--font-body)" }}>
              {error}
            </p>
          )}
          {!loading && !error && data && data.lines.length === 0 && (
            <p className="text-text-secondary" style={{ fontSize: "var(--font-body)" }}>
              No matching lines in the scanned log tail.
            </p>
          )}
          {!error && data && data.lines.length > 0 && displayLines.length === 0 && grepActive && (
            <p className="text-text-secondary" style={{ fontSize: "var(--font-body)" }}>
              No lines match grep {grepLabel}.
            </p>
          )}
          {!error && displayLines.length > 0 && (
            <div className="space-y-2">
              {displayLines.map(({ line, index }) => {
                const parsed = formatLine(line);
                const activeOpen = source === "ws" && activeWsOpenIndices.has(index);
                const stale = !activeOpen && source === "ocpp" && isLogLineStale(line, nowMs);
                const parsedBody =
                  parseMeterValues && source === "ocpp" && parsed.body
                    ? formatMeterValuesLogBody(parsed.body, prevMeterValuesByIndex.get(index), {
                        bcu12,
                        logTsMs: parseLogLineTs(line),
                      })
                    : null;
                const pnLine =
                  displayPn && parsedBody ? formatPnLine(index, mvIndexList) : null;
                const bodyText = parsedBody
                  ? pnLine
                    ? `${parsedBody}\n${pnLine}`
                    : parsedBody
                  : (parsed.body ?? "");
                return (
                  <pre
                    key={`${index}-${parsed.head}`}
                    className={[
                      "overflow-x-auto rounded-md border border-border-subtle px-2 py-1.5 whitespace-pre-wrap break-all font-mono text-text-primary",
                      activeOpen
                        ? "border-status-ok/20"
                        : stale
                          ? "border-status-error/15"
                          : "bg-page",
                    ].join(" ")}
                    style={{
                      fontSize: "11px",
                      lineHeight: 1.35,
                      ...(activeOpen
                        ? { backgroundColor: "rgba(40, 167, 69, 0.08)" }
                        : stale
                          ? { backgroundColor: "rgba(220, 53, 69, 0.07)" }
                          : {}),
                    }}
                  >
                    <span className="text-text-secondary">{parsed.head}</span>
                    {parsed.dir && (
                      <span className={parsed.dir === "in" ? "text-status-ok" : "text-accent"}>
                        {` ${parsed.dir} `}
                      </span>
                    )}
                    {bodyText}
                  </pre>
                );
              })}
            </div>
          )}
        </div>

        <footer
          className="flex flex-wrap items-center justify-between gap-2 border-t border-border-subtle px-4 py-2 text-text-secondary"
          style={{ fontSize: "var(--font-micro)" }}
        >
          {data && (
            <>
              <span>
                {grepActive
                  ? `${displayLines.length} / ${data.lines.length} lines`
                  : `${data.lines.length} line${data.lines.length === 1 ? "" : "s"}`}
                {" · newest first"}
                {grepActive ? " · filtered" : ""}
                {data.truncated ? " · partial file scan" : ""}
              </span>
              <div className="flex min-w-0 flex-wrap items-center justify-end gap-2">
                {onChargerIdChange && (
                  <input
                    type="text"
                    value={altCharger}
                    onChange={(e) => setAltCharger(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        openAltCharger();
                      }
                    }}
                    placeholder={ALT_CHARGER_PLACEHOLDER}
                    title={ALT_CHARGER_TITLE}
                    aria-label={ALT_CHARGER_TITLE}
                    className="min-w-[12rem] max-w-md flex-1 rounded-md border border-border-subtle bg-page px-2.5 py-1 font-mono text-text-primary outline-none focus:border-accent"
                    style={{ fontSize: "var(--font-micro)" }}
                    spellCheck={false}
                  />
                )}
                {data.can_load_more && (
                  <button
                    type="button"
                    onClick={() => loadMore()}
                    disabled={loadingMore}
                    className="shrink-0 rounded-md border border-border-subtle bg-page px-2.5 py-1 font-medium text-accent hover:border-accent disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    {loadingMore ? "Loading…" : "Load more (+200)"}
                  </button>
                )}
              </div>
            </>
          )}
        </footer>
      </aside>
    </div>
  );
}
